/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

interface Enrollment {
  _id: string;
  name: string;
  email: string;
  mobile: string;
  course: string;
  courseName: string;
  country: string;
  amount: string;
  trainingProgramName: string;
  currency: string;
  razorpay_payment_id: string;
  createdAt: string;
}

const INACTIVITY_TIMEOUT: number = 15 * 60 * 1000; // 15 minutes

const AdminEnrollments = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        if (!token) {
          setError("No authentication token found");
          return;
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}admin/enrollments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error("Failed to fetch enrollments");
        }

        const data = await response.json();
        setEnrollments(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load enrollments",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, []);

  const filteredEnrollments = enrollments.filter(
    (enrollment) =>
      enrollment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.mobile?.includes(searchTerm) ||
      enrollment.trainingProgramName
        ?.toLowerCase()
        ?.includes(searchTerm.toLowerCase()) ||
      enrollment.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.country?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedEnrollments = [...filteredEnrollments].sort((a, b) => {
    let aValue: any = a[sortBy as keyof typeof a];
    let bValue: any = b[sortBy as keyof typeof b];

    // Handle amount sorting (convert to number)
    if (sortBy === "amount") {
      aValue = Number.parseFloat(aValue);
      bValue = Number.parseFloat(bValue);
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedEnrollments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEnrollments = sortedEnrollments.slice(startIndex, endIndex);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    window.location.reload();
  };

  // 🔹 Inactivity Timer
  useEffect(() => {
    let timeout: any;

    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        handleLogout();
      }, INACTIVITY_TIMEOUT);
    };

    // List of events to detect activity
    const events = ["mousemove", "keydown", "click", "scroll"];

    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer(); // start timer

    return () => {
      clearTimeout(timeout);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, []);

  // 🔹 Fetch Enrollments
  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        if (!token) {
          setError("No authentication token found");
          return;
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}admin/enrollments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch enrollments");
        }

        const data = await response.json();
        setEnrollments(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load enrollments",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-teal-600"></div>
          <p className="text-slate-600">Loading enrollments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mb-4 text-red-600">⚠️</div>
          <p className="mb-2 font-medium text-slate-900">
            Error loading enrollments
          </p>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl bg-white p-8 shadow-lg"
        >
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-3xl font-bold text-slate-900">
              Enrollment Records
            </h2>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search enrollments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 pr-10 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500 sm:w-64"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg
                    className="h-4 w-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
                    />
                  </svg>
                </div>
              </div>

              {/* Total Count */}
              <div className="rounded-xl bg-teal-50 px-4 py-2 text-sm font-medium text-teal-800">
                Total: {filteredEnrollments.length} enrollments
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Enrollments Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th
                    className="cursor-pointer px-4 py-3 text-left font-semibold text-slate-700 hover:text-teal-600"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {sortBy === "name" && (
                        <span className="text-teal-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-4 py-3 text-left font-semibold text-slate-700 hover:text-teal-600"
                    onClick={() => handleSort("mobile")}
                  >
                    <div className="flex items-center gap-2">
                      Number
                      {sortBy === "mobile" && (
                        <span className="text-teal-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-4 py-3 text-left font-semibold text-slate-700 hover:text-teal-600"
                    onClick={() => handleSort("amount")}
                  >
                    <div className="flex items-center gap-2">
                      Amount
                      {sortBy === "amount" && (
                        <span className="text-teal-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-4 py-3 text-left font-semibold text-slate-700 hover:text-teal-600"
                    onClick={() => handleSort("trainingProgramName")}
                  >
                    <div className="flex items-center gap-2">
                      Training Program
                      {sortBy === "trainingProgramName" && (
                        <span className="text-teal-600">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    Course
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedEnrollments.map((enrollment, index) => (
                  <motion.tr
                    key={enrollment._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border-b border-slate-100 transition-colors hover:bg-slate-50"
                  >
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-900">
                        {enrollment.name}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-slate-600">{enrollment.mobile}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-semibold text-teal-600">
                        {enrollment.currency}{" "}
                        {Number(enrollment.amount).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="rounded-full bg-teal-100 px-3 py-1 text-sm font-medium text-teal-800">
                        {enrollment.trainingProgramName}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-xs text-sm text-slate-600">
                        <div
                          className="truncate font-medium"
                          title={enrollment.courseName}
                        >
                          {enrollment.courseName}
                        </div>
                        <div className="mt-1 text-xs capitalize text-slate-500">
                          📍 {enrollment.country}
                        </div>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>

            {filteredEnrollments.length === 0 && (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <svg
                    className="h-6 w-6 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-medium text-slate-900">
                  No enrollments found
                </h3>
                <p className="text-slate-500">
                  {searchTerm
                    ? "Try adjusting your search terms."
                    : "No enrollment records available."}
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, sortedEnrollments.length)} of{" "}
                {sortedEnrollments.length} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          currentPage === page
                            ? "bg-teal-600 text-white"
                            : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Summary Statistics */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl bg-gradient-to-r from-teal-50 to-teal-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-teal-600">
                    Total Enrollments
                  </p>
                  <p className="text-2xl font-bold text-teal-800">
                    {enrollments.length}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-200">
                  <svg
                    className="h-6 w-6 text-teal-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-gradient-to-r from-slate-50 to-slate-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Total Revenue
                  </p>
                  <p className="text-2xl font-bold text-slate-800">
                    USD{" "}
                    {enrollments
                      .reduce(
                        (sum, enrollment) =>
                          sum + Number.parseFloat(enrollment.amount),
                        0,
                      )
                      .toLocaleString()}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
                  <svg
                    className="h-6 w-6 text-slate-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-gradient-to-r from-blue-50 to-blue-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">
                    Programs Active
                  </p>
                  <p className="text-2xl font-bold text-blue-800">
                    {
                      new Set(enrollments.map((e) => e.trainingProgramName))
                        .size
                    }
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-200">
                  <svg
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 11-4 0 2 2 0 014 0m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminEnrollments;
